/**
 * Phase 3.39 — Top-Dealer Leaderboard.
 *
 * Öffentliche Seite ``/leaderboard`` mit Hybrid-Score-Ranking. Score kombiniert
 * Rating, Listings-Volumen, Reaktionszeit und Aktivität der letzten 90 Tage.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Badge } from "../components/ui/badge";
import { Trophy, Star, MapPin, Building2, Wrench, Zap, Award, ShieldCheck, ArrowRight, Loader2 } from "lucide-react";

const RANK_COLORS = ["bg-[#FFCC00] text-[#111827]", "bg-gray-200 text-[#111827]", "bg-orange-400 text-white"];

export default function LeaderboardPage() {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        api.get("/dealers/leaderboard?limit=20")
            .then((r) => setData(r.data))
            .catch((e) => setError(e.response?.data?.detail || "Fehler beim Laden"));
    }, []);

    if (error) {
        return <div className="max-w-4xl mx-auto px-4 py-12 text-red-700" data-testid="leaderboard-error">{error}</div>;
    }
    if (!data) {
        return (
            <div className="max-w-4xl mx-auto px-4 py-12 flex items-center gap-3 text-gray-600" data-testid="leaderboard-loading">
                <Loader2 className="w-5 h-5 animate-spin" /> Leaderboard wird geladen…
            </div>
        );
    }

    return (
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-8 md:py-12" data-testid="leaderboard-page">
            <div className="mb-8">
                <div className="text-xs font-bold uppercase tracking-wider text-[#FFCC00] mb-2 flex items-center gap-2">
                    <Trophy className="w-4 h-4" /> Top-Händler
                </div>
                <h1 className="font-display text-3xl md:text-5xl font-black tracking-tight">Leaderboard</h1>
                <p className="text-gray-600 mt-2 max-w-3xl">
                    Unsere besten Händler sortiert nach <strong>Hybrid-Score</strong> — einer Kombination aus Bewertungen,
                    Inserate-Volumen, Reaktionszeit und Aktivität der letzten 90 Tage.
                </p>
            </div>

            {data.items.length === 0 ? (
                <div className="bg-white border border-gray-200 rounded-sm p-8 text-center text-gray-500" data-testid="leaderboard-empty">
                    Noch keine bewerteten Händler im Ranking.
                </div>
            ) : (
                <div className="space-y-3">
                    {data.items.map((d, i) => (
                        <Link
                            key={d.id}
                            to={`/dealer/${d.id}`}
                            className="flex items-stretch gap-3 bg-white border border-gray-200 hover:border-[#111827] hover:shadow-md transition-all rounded-sm overflow-hidden group"
                            data-testid={`leaderboard-row-${i + 1}`}
                        >
                            <div className={`w-12 md:w-16 flex flex-col items-center justify-center font-display font-black text-2xl md:text-3xl ${RANK_COLORS[i] || "bg-gray-100 text-gray-700"}`}>
                                #{i + 1}
                            </div>
                            <div className="flex-1 p-4 min-w-0">
                                <div className="flex items-start gap-3">
                                    {d.company_logo_url ? (
                                        <img src={d.company_logo_url} alt="" className="w-12 h-12 rounded-sm object-cover bg-gray-100" />
                                    ) : (
                                        <div className="w-12 h-12 bg-[#111827] text-[#FFCC00] flex items-center justify-center rounded-sm font-display font-black text-xl flex-shrink-0">
                                            {(d.company_name || "?").charAt(0)}
                                        </div>
                                    )}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap mb-1">
                                            <h2 className="font-display font-black text-lg md:text-xl text-[#111827] truncate" data-testid={`leaderboard-name-${i + 1}`}>{d.company_name}</h2>
                                            {d.verified && (
                                                <Badge className="bg-green-100 text-green-800 hover:bg-green-100 gap-1 rounded-sm uppercase tracking-wider text-[9px] font-bold">
                                                    <ShieldCheck className="w-3 h-3" /> Verifiziert
                                                </Badge>
                                            )}
                                            {d.responds_fast && (
                                                <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100 gap-1 rounded-sm uppercase tracking-wider text-[9px] font-bold" data-testid={`leaderboard-fast-${i + 1}`}>
                                                    <Zap className="w-3 h-3" /> Antwortet schnell
                                                </Badge>
                                            )}
                                            {d.dealer_workshop && (
                                                <Badge className="bg-orange-100 text-orange-800 hover:bg-orange-100 gap-1 rounded-sm uppercase tracking-wider text-[9px] font-bold">
                                                    <Wrench className="w-3 h-3" /> Werkstatt
                                                </Badge>
                                            )}
                                        </div>
                                        <div className="flex flex-wrap gap-3 text-xs text-gray-600">
                                            {d.city && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3 text-gray-400" />{d.city}</span>}
                                            <span className="inline-flex items-center gap-1"><Building2 className="w-3 h-3 text-gray-400" />{d.listings_count} Inserate</span>
                                            {d.rating_count > 0 && (
                                                <span className="inline-flex items-center gap-1" data-testid={`leaderboard-rating-${i + 1}`}>
                                                    <Star className="w-3 h-3 fill-[#FFCC00] text-[#FFCC00]" />
                                                    <span className="font-mono-num font-bold">{d.rating_avg}</span>
                                                    <span>({d.rating_count})</span>
                                                </span>
                                            )}
                                            {d.recent_reviews_90d > 0 && (
                                                <span className="text-gray-500">{d.recent_reviews_90d} Reviews / 90T</span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="hidden md:flex flex-col items-end shrink-0">
                                        <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 flex items-center gap-1">
                                            <Award className="w-3 h-3" /> Hybrid-Score
                                        </div>
                                        <div className="font-display font-black text-2xl text-[#111827] font-mono-num" data-testid={`leaderboard-score-${i + 1}`}>{d.hybrid_score}</div>
                                        <div className="text-[9px] text-gray-400 font-mono-num">/ 100</div>
                                    </div>
                                </div>
                                <div className="md:hidden mt-3 flex items-center justify-between border-t border-gray-100 pt-2">
                                    <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Hybrid-Score</span>
                                    <span className="font-mono-num font-bold text-[#111827]">{d.hybrid_score} <span className="text-gray-400">/100</span></span>
                                </div>
                                <div className="mt-3 flex items-center justify-end text-xs font-bold uppercase tracking-wider text-[#111827] group-hover:translate-x-1 transition-transform">
                                    Händler-Profil <ArrowRight className="w-3 h-3 ml-1" />
                                </div>
                            </div>
                        </Link>
                    ))}
                </div>
            )}

            <div className="mt-8 bg-gray-50 border border-gray-200 rounded-sm p-5 text-xs text-gray-600 leading-relaxed" data-testid="leaderboard-explainer">
                <strong className="text-[#111827]">So wird der Hybrid-Score berechnet:</strong>
                <ul className="mt-2 space-y-1 list-disc pl-5">
                    <li><strong>40 %</strong> Durchschnitts-Bewertung (Skala 0–5★)</li>
                    <li><strong>30 %</strong> Anzahl aktiver Inserate (gedeckelt bei 50)</li>
                    <li><strong>15 %</strong> Reaktionszeit auf Bewertungen (&lt; 24 h ⇒ 100)</li>
                    <li><strong>15 %</strong> Neue Bewertungen in den letzten 90 Tagen (gedeckelt bei 10)</li>
                </ul>
                <p className="mt-2">Das Ranking aktualisiert sich live mit jeder neuen Bewertung oder Antwort.</p>
            </div>
        </div>
    );
}
